import { useFormikContext } from 'formik';
import { ChangeEvent, ReactElement, useEffect } from 'react';
import { NeglectedOrDelinquentInformationFormValues } from '../../../types/NeglectedOrDelinquentInformationModel';
import { PanelType } from '../../../types/PanelType';
import { FormRadioListOrientation } from '../../../types/propTypes/FormRadioListPropTypes';
import { SelectOption } from '../../../types/SelectOption';
import { YES_NO_OPTIONS } from '../../../types/Constants';
import { createNeglectedOrDelinquentInformationValidationSchema } from '../../../types/Validation/NeglectedOrDelinquentInformationValidationSchema';
import FormDropdown from '../../FormInputs/FormDropdown/FormDropdown';
import FormInputLabel from '../../FormInputs/FormInputLabel/FormInputLabel';
import FormRadioList from '../../FormInputs/FormRadioList/FormRadioList';
import Panel from '../../Panel/Panel';

export const NORD_FUNDING_QUESTION_1 =
    'Did the State Agency receive Title I, Part D, Subpart 1 funding ' +
    'and serve students during the selected school year?';
export const NORD_FUNDING_QUESTION_2 =
    'Did the LEA receive Title I, Part D, Subpart 2 funding ' +
    'and serve students during the selected school year?';

interface NeglectedOrDelinquentLEAInformationPanelProps {
    leaNameDisplay: string;
    schoolYearDisplay: string;
    facilityTypes: SelectOption[];
    disabled: boolean;
    isStateOperatedAgency: boolean;
    validationSchema: ReturnType<
        typeof createNeglectedOrDelinquentInformationValidationSchema
    >;
}

const NeglectedOrDelinquentLEAInformationPanel = ({
    leaNameDisplay,
    schoolYearDisplay,
    facilityTypes,
    disabled,
    isStateOperatedAgency,
    validationSchema,
}: NeglectedOrDelinquentLEAInformationPanelProps): ReactElement => {
    const { values, setFieldValue, validateForm } =
        useFormikContext<NeglectedOrDelinquentInformationFormValues>();
    useEffect(() => {
        void validateForm(values);
    }, [values, validateForm]);
    const showFacilityType = isStateOperatedAgency
        ? values.fundingReceived1 === 'Yes'
        : values.fundingReceived2 === 'Yes';

    const handleFundingClick = (event: ChangeEvent<HTMLButtonElement>): void => {
        if (event.target.value !== 'Yes') {
            setFieldValue('facilityType', '', false);
        }
    };

    return (
        <Panel panelType={PanelType.INFO} heading="LEA Information">
            <div className="field-row field-row-grid-2-1">
                <div className="field-container disabled">
                    <FormInputLabel
                        field="leaNameDisplay"
                        displayName="LEA Name"
                        disabled
                    />
                    <input
                        id="leaNameDisplay"
                        className="data-input"
                        type="text"
                        disabled
                        readOnly
                        value={leaNameDisplay}
                        data-testid="form-readonly-lea-name"
                    />
                </div>
                <div className="field-container disabled">
                    <FormInputLabel
                        field="schoolYearDisplay"
                        displayName="School Year"
                        disabled
                    />
                    <input
                        id="schoolYearDisplay"
                        className="data-input"
                        type="text"
                        disabled
                        readOnly
                        value={schoolYearDisplay}
                        data-testid="form-readonly-school-year"
                    />
                </div>
            </div>
            {isStateOperatedAgency ? (
                <FormRadioList
                    field="fundingReceived1"
                    displayName={NORD_FUNDING_QUESTION_1}
                    options={YES_NO_OPTIONS}
                    optionOrientation={FormRadioListOrientation.Horizontal}
                    disabled={disabled}
                    validationSchema={validationSchema}
                    onClick={handleFundingClick}
                />
            ) : (
                <FormRadioList
                    field="fundingReceived2"
                    displayName={NORD_FUNDING_QUESTION_2}
                    options={YES_NO_OPTIONS}
                    optionOrientation={FormRadioListOrientation.Horizontal}
                    disabled={disabled}
                    validationSchema={validationSchema}
                    onClick={handleFundingClick}
                />
            )}
            {showFacilityType && (
                <FormDropdown
                    field="facilityType"
                    displayName="What type of facility received funding?"
                    options={facilityTypes.filter((option) => !option.isDisabled)}
                    isMulti={false}
                    disabled={disabled}
                    isClearable={false}
                    validationSchema={validationSchema}
                    classConfig={{ containerClasses: 'nord-facility-type' }}
                />
            )}
        </Panel>
    );
};

export default NeglectedOrDelinquentLEAInformationPanel;
